'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface MarketingOnboardingBannerProps {
  type: 'top' | 'hero';
  userType?: 'patient' | 'provider';
}

const ONBOARDING_STORAGE_KEY = 'right2hope_onboarding';
const PROMO_BANNER_SESSION_KEY = 'promoBannerClosed';
const PROMO_BANNER_HEIGHT_CLASS = 'h-14'; // 56px

export default function MarketingOnboardingBanner({
  type,
  userType,
}: MarketingOnboardingBannerProps) {
  const router = useRouter();
  const pathname = usePathname();

  const resolvedUserType = useMemo<'patient' | 'provider'>(() => {
    if (userType) return userType;
    if (pathname.startsWith('/about/providers')) return 'provider';
    return 'patient';
  }, [pathname, userType]);

  // `isDismissed` decides whether this banner renders at all, and this component sits
  // at the top of every marketing route, all of which are server-rendered. Reading
  // sessionStorage in the initializer would make the server render the banner while a
  // returning visitor's first client render dropped it — a hydration mismatch. Start
  // dismissed-false on both sides and apply the stored preference after mount.
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    setIsDismissed(window.sessionStorage.getItem(PROMO_BANNER_SESSION_KEY) === 'true');
  }, []);

  const dismiss = useCallback(() => {
    try {
      window.sessionStorage.setItem(PROMO_BANNER_SESSION_KEY, 'true');
    } catch {
      // ignore
    }
    setIsDismissed(true);
  }, []);

  // Keep layout offset consistent with app onboarding banner behavior.
  useEffect(() => {
    if (type !== 'top') return;
    if (typeof document === 'undefined') return;

    if (!isDismissed) {
      document.body.classList.add('promo-banner-visible');
    } else {
      document.body.classList.remove('promo-banner-visible');
    }

    return () => {
      document.body.classList.remove('promo-banner-visible');
    };
  }, [isDismissed, type]);

  const startOnboarding = useCallback(() => {
    try {
      window.localStorage.setItem(
        ONBOARDING_STORAGE_KEY,
        JSON.stringify({
          isActive: true,
          userType: resolvedUserType,
          currentStep: 'signup',
          startedAt: new Date().toISOString(),
        }),
      );
    } catch {
      // ignore
    }

    router.push(`/users/auth/${resolvedUserType}?onboarding=true`);
  }, [resolvedUserType, router]);

  // Marketing pages are unauthenticated, so we always show unless dismissed (top banner only).
  if (type === 'top') {
    if (isDismissed) return null;

    const bannerText =
      resolvedUserType === 'provider'
        ? 'Help your patients find the right treatment programs.'
        : 'Ready to find the right treatment?';

    return (
      <div
        className={`fixed top-0 left-0 right-0 w-full bg-primary text-white px-4 z-[51] flex items-center justify-between ${PROMO_BANNER_HEIGHT_CLASS}`}
      >
        <div className="flex-1 flex items-center space-x-2 sm:space-x-4 overflow-hidden">
          <div className="flex flex-col text-left">
            <p className="text-sm text-white truncate">
              <span className="font-semibold text-white">{bannerText}</span> Upload medical records
              & instantly get program matches.
            </p>
          </div>
        </div>

        <div className="flex items-center ml-2 sm:ml-4 flex-shrink-0">
          <button
            type="button"
            onClick={startOnboarding}
            className="bg-background text-primary hover:bg-default-200 transition-colors duration-200 font-semibold text-xs sm:text-sm px-2 sm:px-3 py-2 rounded-full whitespace-nowrap mr-2 sm:mr-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            Sign Up & Upload
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="p-1 text-white transition-colors duration-200 hover:text-default-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            aria-label="Close banner"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div key="upload-prompt" className="py-8 bg-default-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-6 lg:gap-8">
          <div className="lg:col-span-4 self-center">
            <h3 className="mb-0 mt-1 text-left sm:whitespace-nowrap text-primary">
              Get Instantly Matched
            </h3>
          </div>
          <div className="lg:col-span-5 self-center">
            <p className="text-primary text-left">
              Upload your <span className="font-semibold">medical records</span>, and our platform
              will quickly match you with suitable treatment programs.
            </p>
          </div>
          <div className="lg:col-span-3 flex lg:justify-end self-center">
            <button type="button" className="rh-btn-black" onClick={startOnboarding}>
              Upload Records & Find Matches
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
